'use client';

import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { receptionQueries } from '../services/reception.queries';
import { useVerifyQrMutation } from '../services/reception.mutations';
import type { ReceptionSession } from '../types';
import { StepDataVerification } from './step-data-verification';

interface ReceptionWizardProps {
  shipmentId: number;
}

export function ReceptionWizard({ shipmentId }: ReceptionWizardProps) {
  const t = useTranslations('reception');
  const [localSession, setLocalSession] = useState<ReceptionSession | null>(
    null
  );

  const didInitSessionRef = useRef(false);

  const { data: shipmentResponse, isLoading } = useQuery(
    receptionQueries.detail(shipmentId)
  );

  const shipment = shipmentResponse?.data ?? null;
  const session = localSession ?? shipment?.active_session ?? null;

  const verifyQrSilentMutation = useVerifyQrMutation({ silent: true });

  // Auto-create session silently if none exists
  useEffect(() => {
    if (!shipment || didInitSessionRef.current) return;
    if (!shipment.active_session) {
      didInitSessionRef.current = true;
      verifyQrSilentMutation.mutate(shipmentId);
    } else {
      didInitSessionRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipment, shipmentId]);

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-16'>
        <div className='text-muted-foreground'>{t('loading')}</div>
      </div>
    );
  }

  if (!shipment) {
    return (
      <div className='flex items-center justify-center py-16'>
        <div className='text-muted-foreground'>{t('shipmentNotFound')}</div>
      </div>
    );
  }

  // Loading state while silently creating the session
  if (!session && !verifyQrSilentMutation.isError) {
    return (
      <div className='flex items-center justify-center py-16'>
        <Loader2 className='text-muted-foreground h-6 w-6 animate-spin' />
        <span className='text-muted-foreground ms-3'>{t('loading')}</span>
      </div>
    );
  }

  // Error state if silent session creation failed
  if (verifyQrSilentMutation.isError && !session) {
    return (
      <div className='flex flex-col items-center justify-center gap-4 py-16'>
        <p className='text-muted-foreground text-sm'>
          {t('errorLoadingSession')}
        </p>
        <Button
          variant='outline'
          onClick={() => verifyQrSilentMutation.mutate(shipmentId)}
        >
          {t('retry')}
        </Button>
      </div>
    );
  }

  return (
    <div className='flex flex-1 flex-col items-center space-y-6'>
      {/* Top bar: Title + Session badge */}
      <div className='flex w-full items-start justify-between'>
        <div>
          <h1 className='text-2xl font-bold tracking-tight'>
            {t('pageTitle')}
          </h1>
          <p className='text-muted-foreground mt-1 text-sm'>
            {t('wizardSubtitleDataVerification')}
          </p>
        </div>
        <div className='flex items-center gap-3'>
          <Badge
            variant='outline'
            className='border-primary/30 bg-primary/5 text-primary gap-1.5 px-3 py-1.5'
          >
            <span className='bg-primary h-2 w-2 rounded-full' />
            {t('sessionActive')}
          </Badge>
        </div>
      </div>

      {session && (
        <StepDataVerification
          session={session}
          onSessionUpdate={setLocalSession}
        />
      )}
    </div>
  );
}
